import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import useMartyContext from 'hooks/useMartyContext';
import { fetchProductDetail } from 'actions/productDetail';
import { makeAscii } from 'helpers';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import MelodyModal from 'components/common/MelodyModal';
import Card from 'components/common/card/Card';
import CardMedia from 'components/common/card/CardMedia';
import CardPrice from 'components/common/card/CardPrice';
import SingleStarRating from 'components/common/SingleStarRating';
import SponsoredBanner from 'components/common/SponsoredBanner';
import getCardData from 'data/getCardData';
import useHover from 'hooks/useHover';
import useColorSwatches from 'hooks/useColorSwatches';
import useCrossSite from 'hooks/useCrossSite';
import { toBool } from 'helpers/DataFormatUtils';
import Hearts from 'components/common/Hearts';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';

import css from 'styles/components/common/productCard.scss';
import { modal } from 'styles/components/search/crossSiteProducts.scss';

const ProductCard = props => {
  const {
    brandName,
    isSponsored,
    isCouture,
    isNew,
    fetchProductDetail,
    onClick,
    productName,
    productSeoUrl,
    productUrl,
    styleColor,
    storeName,
    styleId
  } = props;

  const { testId } = useMartyContext();
  const [ref, setRef] = useState(null);
  const { swatchImageId, markup: colorSwatchMarkup } = useColorSwatches({ ...props });

  const { isHovered } = useHover(ref);
  const { onClick: crossSiteClick, closeClick, content, visible } = useCrossSite(props, fetchProductDetail);
  const { hearts, media, price, reviews } = getCardData({ ...props, isHovered, swatchImageId });

  const productLink = productSeoUrl || productUrl || null;
  const encodedBrandName = makeAscii(brandName);
  const encodedProductName = makeAscii(productName);
  const productLabel = `${encodedBrandName} - ${encodedProductName}. Color ${styleColor}.`;

  let flag = null;
  if (isSponsored) {
    flag = <SponsoredBanner newCard={true}/>;
  } else if (toBool(isCouture) && !storeName) {
    flag = <p className={css.styleRoom} data-test-id={testId('styleRoomBanner')}>Style Room</p>;
  } else if (storeName && styleId) {
    flag = <div className={css.isTrusted}><TrustedRetailerBanner styleId={styleId}/></div>;
  } else if (toBool(isNew)) {
    flag = <p className={css.new} data-test-id={testId('newBanner')}>New</p>;
  }

  const modalProps = {
    onRequestClose: () => closeClick(),
    className: modal,
    contentLabel: 'Trusted Retailers',
    isOpen: !!visible
  };

  return (
    <Card>
      {visible && <MelodyModal {...modalProps}>{content}</MelodyModal>}
      <Link
        className={css.productLink}
        to={productLink}
        itemProp="url"
        data-test-id={testId('searchResultLink')}
        onClick={crossSiteClick || onClick}
        innerRef={setRef}>
        {productLabel} {price.label}. {reviews.label}
      </Link>
      <CardMedia {...media}>
        <div className={css.top}>
          {flag}
          <Hearts {...hearts} />
        </div>
        <div className={css.bottom}>
          {colorSwatchMarkup}
          {storeName && <div className={css.storeWrapper}><CrossSiteLabel storeName={storeName}/></div>}
        </div>
      </CardMedia>
      <dl className={css.details}>
        <SingleStarRating {...reviews}/>
        <dt>Brand Name</dt>
        <dd
          className={css.brandName}
          itemProp="brand"
          itemScope
          itemType="http://schema.org/Brand"
          data-test-id={testId('brand')}>
          {encodedBrandName}
        </dd>
        <dt>Product Name</dt>
        <dd className={css.productName} itemProp="name" data-test-id={testId('productName')}>{encodedProductName}</dd>
        <dt>Color</dt>
        <dd className={css.colorName} itemProp="color" data-test-id={testId('colorName')}>{styleColor}</dd>
        <CardPrice {...price} />
      </dl>
    </Card>
  );
};

const mapDispatchToProps = {
  fetchProductDetail
};

export default connect(null, mapDispatchToProps)(ProductCard);
